/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { useIntl } from 'react-intl';

import { ThemeProvider } from '@atlaskit/app-provider/theme-provider';
import { jsx } from '@atlaskit/css';
import Image from '@atlaskit/image/image';
import { SpotlightActions } from '@atlaskit/spotlight/actions';
import { SpotlightBody } from '@atlaskit/spotlight/body';
import { SpotlightCard } from '@atlaskit/spotlight/card';
import { SpotlightControls } from '@atlaskit/spotlight/controls';
import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';
import { SpotlightFooter } from '@atlaskit/spotlight/footer';
import { SpotlightHeader } from '@atlaskit/spotlight/header';
import { SpotlightHeadline } from '@atlaskit/spotlight/headline';
import { SpotlightMedia } from '@atlaskit/spotlight/media';
import { PopoverContent } from '@atlaskit/spotlight/popover-content';
import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';
import { PopoverTarget } from '@atlaskit/spotlight/popover-target';
import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';

// eslint-disable-next-line import/extensions
import skillsSpotlightIllustration from './assets/skills-spotlight.svg';
import messages from './messages';

/**
 * How the spotlight was dismissed. `light-dismiss` is ADS's term for the implicit close: clicking
 * outside the card, or pressing Escape while focus is inside it.
 */
export type DismissSource = 'cross-icon' | 'light-dismiss';

type SkillsOnboardingSpotlightProps = {
	isVisible: boolean;
	/**
	 * Called when the primary "View skills" CTA is pressed. The controller wires this to the
	 * existing insert-`/` handler (`onDiscoverSkillsClick`) plus lifecycle/dismiss handling.
	 */
	onViewSkills: () => void;
	/** Called when the spotlight is dismissed, either explicitly (X) or by a light dismiss. */
	onDismiss: (source: DismissSource) => void;
	/**
	 * The element the spotlight anchors to. Wrapped in a `PopoverTarget`; the card floats next to
	 * it via the `placement`/`offset` below. Anchor this to the chat input (like the @Mentions
	 * spotlight) or the "View skills" control, matching the mock.
	 */
	children: React.ReactNode;
};

// Placed to the left of the Rovo chat panel (the anchor is the chat input), pointing right at it
const OFFSET: [number, number] = [0, 12];

export const SkillsOnboardingSpotlight = ({
	isVisible,
	onViewSkills,
	onDismiss,
	children,
}: SkillsOnboardingSpotlightProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<PopoverProvider>
			<PopoverTarget>{children}</PopoverTarget>
			<PopoverContent
				// Fires for any click outside the card, including closing the Rovo panel, and for Escape.
				// The X control has its own handler below.
				dismiss={() => onDismiss('light-dismiss')}
				isVisible={isVisible}
				placement="left-start"
				offset={OFFSET}
			>
				<ThemeProvider defaultColorMode="light">
					<SpotlightCard testId="skills-onboarding-spotlight">
						<SpotlightHeader>
							<SpotlightHeadline>
								{formatMessage(messages.skillsOnboardingSpotlightTitle)}
							</SpotlightHeadline>
							<SpotlightControls>
								<SpotlightDismissControl onClick={() => onDismiss('cross-icon')} />
							</SpotlightControls>
						</SpotlightHeader>
						<SpotlightMedia>
							<Image src={skillsSpotlightIllustration} alt="" width="100%" />
						</SpotlightMedia>
						<SpotlightBody>{formatMessage(messages.skillsOnboardingSpotlightBody)}</SpotlightBody>
						<SpotlightFooter>
							<SpotlightActions>
								<SpotlightPrimaryAction onClick={onViewSkills}>
									{formatMessage(messages.skillsOnboardingSpotlightViewSkills)}
								</SpotlightPrimaryAction>
							</SpotlightActions>
						</SpotlightFooter>
					</SpotlightCard>
				</ThemeProvider>
			</PopoverContent>
		</PopoverProvider>
	);
};
